import { useCallback, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t, numero } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { useFechar } from '../input/useAtalhos';
import { usePrenderFoco } from '../input/usePrenderFoco';
import { Gump } from './Gump';
import { ItemIcon } from './ItemIcon';

/**
 * O "Quantas?" das pilhas (techspec-pilha-de-pecas §13): arrastar ou separar uma pilha pergunta quantas unidades,
 * como no UO. Enter confirma, Esc cancela.
 */
export interface PedidoQuantas {
  defId: string;
  nome?: string;
  /** O máximo que dá para escolher (a pilha inteira, ou uma a menos ao separar). */
  max: number;
  /** O valor que já vem escrito. Padrão: o máximo. */
  inicial?: number;
  /** O título da caixa (ex.: "Guardar no banco"). */
  titulo: string;
}

type Aberto = PedidoQuantas & { resolver(n: number | null): void };

/**
 * `pedir` devolve a quantidade escolhida, ou null se cancelou. Pilha de 1 (ou máximo 1) não pergunta: devolve 1.
 * O `dialogo` vai no JSX de quem usa (já sai num portal no body).
 */
export function useQuantas(): { pedir(p: PedidoQuantas): Promise<number | null>; dialogo: ReactNode } {
  const [aberto, setAberto] = useState<Aberto | null>(null);
  const pedir = useCallback((p: PedidoQuantas) => {
    if (p.max <= 1) return Promise.resolve(p.max === 1 ? 1 : null);
    return new Promise<number | null>(resolver => {
      setAberto(cur => {
        cur?.resolver(null);
        return { ...p, resolver };
      });
    });
  }, []);
  const fechar = useCallback((n: number | null) => {
    setAberto(cur => {
      cur?.resolver(n);
      return null;
    });
  }, []);
  const dialogo = aberto
    ? createPortal(<QuantasDialogo key={`${aberto.defId}:${aberto.max}`} pedido={aberto} onFechar={fechar} />, document.body)
    : null;
  return { pedir, dialogo };
}

function QuantasDialogo({ pedido, onFechar }: { pedido: Aberto; onFechar(n: number | null): void }) {
  const { max } = pedido;
  const [texto, setTexto] = useState(() => String(Math.min(max, Math.max(1, pedido.inicial ?? max))));
  const ref = useRef<HTMLDivElement>(null);
  usePrenderFoco(ref, true, '[data-foco-inicial]');
  useFechar(() => onFechar(null), { modal: true });

  const n = Number(texto);
  const valido = Number.isInteger(n) && n >= 1 && n <= max;
  const nome = nomeDoItem(pedido.defId, pedido.nome);
  const ajustar = (v: number) => setTexto(String(Math.min(max, Math.max(1, v))));
  const confirmar = (e: FormEvent) => {
    e.preventDefault();
    if (valido) onFechar(n);
  };

  return (
    <div className="modal" role="presentation" onClick={() => onFechar(null)}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="quantas-titulo" onClick={e => e.stopPropagation()}>
        <Gump
          title={pedido.titulo}
          action={
            <button type="button" className="btn btn--tiny btn--ghost" onClick={() => onFechar(null)} aria-label={t('comum.fechar')}>
              ×
            </button>
          }
        >
          <form className="stack quantas" onSubmit={confirmar}>
            <p id="quantas-titulo" className="modal__item">
              <ItemIcon id={pedido.defId} size={36} quantity={max} />
              <span>{t('quantas.pergunta', { item: nome, max: numero(max) })}</span>
            </p>
            <div className="quantas__linha">
              <button type="button" className="btn btn--tiny" onClick={() => ajustar(1)} disabled={n <= 1}>1</button>
              <button type="button" className="btn btn--tiny" onClick={() => ajustar((valido ? n : 1) - 1)} disabled={n <= 1}
                aria-label={t('quantas.menos')}>−</button>
              <input
                className="market-qty quantas__n"
                type="text"
                inputMode="numeric"
                value={texto}
                data-foco-inicial
                onFocus={e => e.currentTarget.select()}
                onChange={e => setTexto(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-label={t('quantas.rotulo', { item: nome })}
                aria-invalid={!valido}
              />
              <button type="button" className="btn btn--tiny" onClick={() => ajustar((valido ? n : 0) + 1)} disabled={n >= max}
                aria-label={t('quantas.mais')}>+</button>
              <button type="button" className="btn btn--tiny" onClick={() => ajustar(max)} disabled={n >= max}>{t('venda.tudo')}</button>
            </div>
            <input
              className="quantas__barra"
              type="range"
              min={1}
              max={max}
              value={valido ? n : 1}
              onChange={e => setTexto(e.target.value)}
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="row row--split">
              <button type="button" className="btn btn--ghost" onClick={() => onFechar(null)}>{t('comum.cancelar')}</button>
              <button type="submit" className="btn btn--primary" disabled={!valido}>{t('quantas.ok')}</button>
            </div>
          </form>
        </Gump>
      </div>
    </div>
  );
}
